
// ABOUTME: Sentry utilities for consistent error tracking and performance monitoring
// ABOUTME: Provides standardized functions for tracing, error capture, and logging

import * as Sentry from "@sentry/react";

// Logger instance for structured logging
export const { logger } = Sentry;

// Standardized error capture with context
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

// UI interaction tracing
export const traceUIInteraction = <T>(
  name: string,
  operation: string,
  fn: () => T,
  attributes?: Record<string, any>
): T => {
  return Sentry.startSpan(
    {
      op: `ui.${operation}`,
      name,
    },
    (span) => {
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }
      return fn();
    }
  );
};

// API call tracing
export const traceAPICall = async <T>(
  name: string,
  fn: () => Promise<T>,
  attributes?: Record<string, any>
): Promise<T> => {
  return Sentry.startSpan(
    {
      op: "http.client",
      name,
    },
    async (span) => {
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }
      try {
        const result = await fn();
        span.setStatus({ code: 1, message: "ok" });
        return result;
      } catch (error) {
        span.setStatus({ code: 2, message: "error" });
        throw error;
      }
    }
  );
};

// Database operation tracing
export const traceDBOperation = async <T>(
  operation: string,
  table: string,
  fn: () => Promise<T>,
  attributes?: Record<string, any>
): Promise<T> => {
  return Sentry.startSpan(
    {
      op: "db.query",
      name: `${operation} ${table}`,
    },
    async (span) => {
      span.setAttribute("db.operation", operation);
      span.setAttribute("db.name", "supabase");
      span.setAttribute("db.table", table);
      
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }
      
      try {
        const result = await fn();
        span.setStatus({ code: 1, message: "ok" });
        return result;
      } catch (error) {
        span.setStatus({ code: 2, message: "error" });
        captureError(error as Error, { table, operation });
        throw error;
      }
    }
  );
};
